import React from 'react';
import {
  Header,
  SpaceBetween,
  Box,
  TextContent,
  ColumnLayout,
  Button,
  AnchorNavigation,
  AppLayout,
  ContentLayout,
  Grid,
  ExpandableSection,
  Container,
} from '@cloudscape-design/components';
import ProductCard from '../../components/ProductCard';
import { useTheme } from '../../context/ThemeContext';
import { useModuleVisit } from '../../hooks/useAnalytics';

const DEFAULT_BANNER = "linear-gradient(135deg, rgb(71, 17, 118) 3%, rgb(131, 57, 157) 44%, rgb(149, 85, 182) 69%, rgb(145, 134, 215) 94%)";
const SUMMIT_BANNER_LIGHT = "center/cover url('/summit/2025_Summits_Kaltura_SessionPage_SessionThumbnail_1@2x.png')";
const SUMMIT_BANNER_DARK = "center/cover url('/summit/2025_Summits_Kaltura_SessionPage_SessionThumbnail_2.png')";

function CodePageContent() {
  const { isSummit, isDark } = useTheme();
  useModuleVisit('code');
  const banner = isSummit ? (isDark ? SUMMIT_BANNER_DARK : SUMMIT_BANNER_LIGHT) : DEFAULT_BANNER;

  const codeStyle = {
    backgroundColor: isDark ? '#1e293b' : '#f8f9fa',
    padding: '1rem',
    borderRadius: '4px',
    overflow: 'auto',
    fontSize: '0.875rem',
    lineHeight: '1.5',
    border: `1px solid ${isDark ? '#334155' : '#e9ebed'}`,
  };

  return (
    <ContentLayout
      defaultPadding
      disableOverlap
      headerBackgroundStyle={banner}
      headerVariant="high-contrast"
      header={
        <Box padding={{ vertical: "xxxl" }}>
          <Grid gridDefinition={[{ colspan: { default: 12, s: 8 } }]}>
            <Box padding="s">
              <Box
                fontSize="display-l"
                fontWeight="bold"
                variant="h1"
                padding="n"
              >
                Built with Kiro
              </Box>
              <Box
                variant="p"
                color="text-body-secondary"
                margin={{ top: "xs", bottom: "l" }}
              >
                ResearchIQ was built using Kiro — an agentic IDE that brings engineering rigor to 
                AI-powered development. From structured specifications to automated hooks, Kiro 
                transformed how this application went from concept to production.
              </Box>
            </Box>
          </Grid>
        </Box>
      }
    >
      <SpaceBetween size="xxl">

        {/* Section 1: What is Kiro */}
        <section id="what-is-kiro" aria-label="What is Kiro">
          <Box margin={{ top: "xl", bottom: "l" }}>
            <hr style={{ border: 'none', borderTop: '1px solid #e9ebed', margin: '3.5rem 0 3.5rem 0' }} />
            <Header variant="h1" margin={{ bottom: "l" }}>
              What is Kiro?
            </Header>
          </Box>

          <Box margin={{ bottom: "l" }}>
            <ul style={{ listStyle: 'none', padding: 0, display: 'flex', justifyContent: 'center' }}>
              <li>
                <ProductCard
                  title="Kiro"
                  vendor="Agentic IDE by AWS"
                  logo="/images/kiro_400X400.jpg"
                  category="Spec-Driven Development | Hooks | Steering | Agents"
                  description="An agentic IDE built on Amazon Bedrock that transforms natural language prompts into detailed specs, working code, documentation, and tests. Go from vibe coding to viable code."
                  href="https://kiro.dev/"
                  buttonText="Learn More"
                />
              </li>
            </ul>
          </Box>

          <TextContent>
            <p>
              Kiro is an agentic IDE that helps developers deliver from concept to production through 
              spec-driven development. While it's great at conversational "vibe coding", its real strength 
              is getting prototypes into production systems with structured specifications, automated hooks, 
              and project-level steering.
            </p>
            <p>
              Built on Code OSS (the open-source foundation of VS Code), Kiro is compatible with existing 
              VS Code settings and Open VSX plugins. It's powered by Amazon Bedrock, giving it access to 
              advanced foundation models for code generation, reasoning, and analysis.
            </p>
          </TextContent>

          <Box margin={{ top: "l" }}>
            <ColumnLayout columns={3} variant="text-grid">
              <div>
                <TextContent>
                  <h3>📋 Specs</h3>
                  <p>
                    Transform a single prompt into structured requirements (with EARS notation), 
                    technical designs (interfaces, schemas, diagrams), and sequenced implementation 
                    tasks — each linked back to requirements so nothing falls through the cracks.
                  </p>
                </TextContent>
              </div>
              <div>
                <TextContent>
                  <h3>⚡ Hooks</h3>
                  <p>
                    Event-driven automations that trigger when you save, create, or delete files. 
                    Like having an experienced developer catching things you miss — auto-updating tests, 
                    refreshing docs, scanning for security issues, enforcing coding standards.
                  </p>
                </TextContent>
              </div>
              <div>
                <TextContent>
                  <h3>🧭 Steering</h3>
                  <p>
                    Project-level guidance files that ensure AI behaviour stays consistent with your 
                    team's conventions, architecture decisions, and coding standards. Committed to Git, 
                    shared across the team.
                  </p>
                </TextContent>
              </div>
            </ColumnLayout>
          </Box>
        </section>

        {/* Section 2: Spec-Driven Development */}
        <div id="spec-driven-development">
          <Box margin={{ top: "xl", bottom: "l" }}>
            <hr style={{ border: 'none', borderTop: '1px solid #e9ebed', margin: '3.5rem 0 3.5rem 0' }} />
            <Header variant="h1" margin={{ bottom: "l" }}>
              Spec-Driven Development
            </Header>
          </Box>

          <TextContent>
            <p>
              Kiro inverts the typical AI coding model: the <strong>spec is the source of truth</strong> and 
              code is a build artifact. This changes how teams version, review, and own behaviour. Instead of 
              endless prompt tweaking, you clearly express what you need, and Kiro delivers better results 
              with fewer iterations.
            </p>
          </TextContent>

          <Box margin={{ top: "l" }}>
            <ColumnLayout columns={2} variant="text-grid">
              <Container header={<Header variant="h3">1. Requirements</Header>}>
                <TextContent>
                  <p>
                    From a single prompt, Kiro generates user stories with EARS (Easy Approach to Requirements 
                    Syntax) acceptance criteria. Edge cases that developers typically handle ad-hoc are made 
                    explicit upfront — so you know the agent is building what you actually want.
                  </p>
                </TextContent>
              </Container>
              <Container header={<Header variant="h3">2. Design</Header>}>
                <TextContent>
                  <p>
                    Kiro analyses your codebase and approved requirements to generate technical designs — 
                    TypeScript interfaces, database schemas, API endpoints, data flow diagrams. This eliminates 
                    the lengthy back-and-forth on requirements clarity that typically slows development.
                  </p>
                </TextContent>
              </Container>
              <Container header={<Header variant="h3">3. Tasks</Header>}>
                <TextContent>
                  <p>
                    Implementation tasks are auto-generated, sequenced by dependency, and linked to requirements. 
                    Each task includes unit tests, integration tests, loading states, and accessibility requirements. 
                    Execute tasks one-by-one with progress tracking and code diff auditing.
                  </p>
                </TextContent>
              </Container>
              <Container header={<Header variant="h3">4. Living Documentation</Header>}>
                <TextContent>
                  <p>
                    Specs stay synced with your evolving codebase. Update code and ask Kiro to refresh specs, 
                    or update specs to regenerate tasks. This solves the common problem where documentation 
                    drifts from implementation during development.
                  </p>
                </TextContent>
              </Container>
            </ColumnLayout>
          </Box>
        </div>

        {/* Section 3: How ResearchIQ Was Built */}
        <div id="how-researchiq-was-built">
          <Box margin={{ top: "xl", bottom: "l" }}>
            <hr style={{ border: 'none', borderTop: '1px solid #e9ebed', margin: '3.5rem 0 3.5rem 0' }} />
            <Header variant="h1" margin={{ bottom: "l" }}>
              How ResearchIQ Was Built
            </Header>
          </Box>

          <TextContent>
            <p>
              Both the frontend and backend of ResearchIQ were developed using Kiro. The spec-driven approach 
              meant that complex features — multi-agent orchestration, citation tracking, real-time WebSocket 
              updates — were planned and designed before implementation, resulting in cleaner architecture and 
              fewer rewrites.
            </p>
          </TextContent>

          <Box margin={{ top: "l" }}>
            <ColumnLayout columns={2} variant="text-grid">
              <div>
                <TextContent>
                  <h3>Frontend Development</h3>
                  <ul>
                    <li>Cloudscape Design System components with dark mode and Summit theme support</li>
                    <li>Real-time agent progress visualisation via WebSockets</li>
                    <li>Interactive citation panels with document source linking</li>
                    <li>Responsive layouts with accessibility compliance</li>
                    <li>Analytics integration for demo engagement tracking</li>
                  </ul>
                </TextContent>
              </div>
              <div>
                <TextContent>
                  <h3>Backend Development</h3>
                  <ul>
                    <li>Multi-agent graph orchestration with Strands Agents SDK</li>
                    <li>Amazon Bedrock Knowledge Base integration for RAG</li>
                    <li>FastAPI with async WebSocket support</li>
                    <li>Market data API integration for real-time enrichment</li>
                    <li>CDK infrastructure as code for deployment</li>
                  </ul>
                </TextContent>
              </div>
            </ColumnLayout>
          </Box>

          <Box margin={{ top: "l" }}>
            <ExpandableSection
              headerText="Specs Used in This Project"
              variant="container"
              headerDescription="Examples of specs that drove ResearchIQ development"
            >
              <TextContent>
                <p>The following specs were created in Kiro to plan and implement key features:</p>
                <ul>
                  <li><strong>research-engine</strong> — Multi-agent graph research workflow with parallel question processing</li>
                  <li><strong>data-storytelling</strong> — Visualisation engine and report generation pipeline</li>
                  <li><strong>agent-visualisation</strong> — Real-time exec briefing progress with telemetry</li>
                  <li><strong>summit-theme-toggle</strong> — Dark mode and Summit 2026 branding with theme persistence</li>
                  <li><strong>insights-agent</strong> — Cross-document pattern analysis and insight extraction</li>
                  <li><strong>observability-deep-dive</strong> — Observability page with Strands and AgentCore content</li>
                  <li><strong>build-vs-buy-talk-track</strong> — Build vs Buy decision framework page</li>
                </ul>
                <p>
                  Each spec follows the same structure: requirements (user stories + acceptance criteria) → 
                  design (architecture, interfaces, data models) → tasks (sequenced, testable, linked to requirements).
                </p>
              </TextContent>
            </ExpandableSection>
          </Box>

          <Box margin={{ top: "l" }}>
            <ExpandableSection
              headerText="Hooks Used in This Project"
              variant="container"
              headerDescription="Automated quality checks running in the background"
            >
              <TextContent>
                <p>Kiro hooks enforce consistency and catch issues automatically:</p>
                <ul>
                  <li><strong>Lint on save</strong> — TypeScript and Python files are linted whenever saved</li>
                  <li><strong>Test refresh</strong> — Related test files are flagged for update when source changes</li>
                  <li><strong>Security scan</strong> — Pre-commit hook scans for leaked credentials and secrets</li>
                  <li><strong>Documentation sync</strong> — API endpoint changes trigger README updates</li>
                </ul>
                <p>
                  Hooks are committed to Git, so the entire team benefits from the same quality checks 
                  without manual setup.
                </p>
              </TextContent>
            </ExpandableSection>
          </Box>
        </div>

        {/* Section 4: Kiro Video */}
        <div id="kiro-in-action">
          <Box margin={{ top: "xl", bottom: "l" }}>
            <hr style={{ border: 'none', borderTop: '1px solid #e9ebed', margin: '3.5rem 0 3.5rem 0' }} />
            <Header variant="h1" margin={{ bottom: "l" }}>
              Kiro in Action
            </Header>
          </Box>

          <Box margin={{ top: "l" }}>
            <ColumnLayout columns={2} variant="text-grid">
              <div>
                <iframe
                  width="100%"
                  height="280"
                  src="https://www.youtube.com/embed/JOqLp1adGO4"
                  title="Go from vibe coding to viable code"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  style={{ borderRadius: '8px', aspectRatio: '16/9' }}
                />
              </div>
              <div>
                <SpaceBetween direction="vertical" size="s">
                  <Header variant="h3">Go from vibe coding to viable code</Header>
                  <TextContent>
                    <p>
                      Kiro turns your prompt into clear requirements, structured designs, implementation 
                      tasks validated by robust tests, and code generated by advanced agents. It's the 
                      flow of AI coding, levelled up with mature engineering practices.
                    </p>
                    <p>
                      Built on Code OSS with MCP support, steering rules for project-wide AI guidance, 
                      and agentic chat for ad-hoc tasks with file, URL, and documentation context providers.
                    </p>
                  </TextContent>
                </SpaceBetween>
              </div>
            </ColumnLayout>
          </Box>

          <Box textAlign="center" margin={{ top: "l" }}>
            <SpaceBetween direction="horizontal" size="m">
              <Button
                variant="primary"
                href="https://kiro.dev/"
                target="_blank"
                iconAlign="right"
                iconName="external"
              >
                Get Kiro
              </Button>
              <Button
                href="https://kiro.dev/docs/"
                target="_blank"
                iconAlign="right"
                iconName="external"
              >
                Documentation
              </Button>
            </SpaceBetween>
          </Box>
        </div>

        {/* Section 5: Development Impact */}
        <div id="development-impact">
          <Box margin={{ top: "xl", bottom: "l" }}>
            <hr style={{ border: 'none', borderTop: '1px solid #e9ebed', margin: '3.5rem 0 3.5rem 0' }} />
            <Header variant="h1" margin={{ bottom: "l" }}>
              Development Impact
            </Header>
          </Box>

          <TextContent>
            <p>
              Kiro's spec-driven approach combined with agentic code generation dramatically 
              accelerated the development of ResearchIQ — both frontend and backend.
            </p>
            <h3>Development Timeline Comparison</h3>
          </TextContent>

          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box padding="s" variant="container">
                <TextContent>
                  <h4>Traditional Development</h4>
                  <ul>
                    <li>Frontend: 3-4 weeks</li>
                    <li>Backend: 4-5 weeks</li>
                    <li>Integration: 1-2 weeks</li>
                    <li>Testing & Debugging: 2-3 weeks</li>
                    <li><strong>Total: 10-14 weeks</strong></li>
                  </ul>
                </TextContent>
              </Box>
            </div>
            <div>
              <Box padding="s" variant="container">
                <TextContent>
                  <h4>Built with Kiro</h4>
                  <ul>
                    <li>Frontend: 3-4 days</li>
                    <li>Backend: 2-3 days</li>
                    <li>Integration: 1 day</li>
                    <li>Testing & Refinement: 2-3 days</li>
                    <li><strong>Total: 8-11 days</strong></li>
                  </ul>
                </TextContent>
              </Box>
            </div>
          </ColumnLayout>

          <TextContent>
            <p>
              This represents a <strong>90%+ reduction in development time</strong> while 
              maintaining high code quality, security standards, and AWS best practices. The 
              spec-driven approach meant less rework — features were designed correctly before 
              implementation began.
            </p>
          </TextContent>
        </div>

      </SpaceBetween>
    </ContentLayout>
  );
}

// Tools component for "On this page" navigation
function CodePageTools() {
  return (
    <div>
      <Header variant="h3">
        <span id="navigation-header">On this page</span>
      </Header>
      <AnchorNavigation
        anchors={[
          {
            text: "What is Kiro?",
            href: "#what-is-kiro",
            level: 1
          },
          {
            text: "Spec-Driven Development",
            href: "#spec-driven-development",
            level: 1
          },
          {
            text: "How ResearchIQ Was Built",
            href: "#how-researchiq-was-built",
            level: 1
          },
          {
            text: "Kiro in Action",
            href: "#kiro-in-action",
            level: 1
          },
          {
            text: "Development Impact",
            href: "#development-impact",
            level: 1
          }
        ]}
        ariaLabelledby="navigation-header"
      />
    </div>
  );
}

function CodePage() {
  return (
    <AppLayout
      content={<CodePageContent />}
      tools={<CodePageTools />}
      toolsHide={false}
      navigationHide={true}
    />
  );
}

export default CodePage;
